import type { DashOptions } from "@-/com.npmjs.better-auth__infra";
import { ENV } from "@-/com.npmjs.varlock/env";

const TRAILING_SLASH_REGEX = /\/$/;

/** @better-auth/infra default Dash API (per the `@type=url` schema comment in `.env.schema`). */
const DEFAULT_BETTER_AUTH_API_URL = "https://dash.better-auth.com";

/** @better-auth/infra default KV service (per the `@type=url` schema comment in `.env.schema`). */
const DEFAULT_BETTER_AUTH_KV_URL = "https://kv.better-auth.com";

function normalizeUrl(url: string): string {
  return url.replace(TRAILING_SLASH_REGEX, "");
}

/** Env value with fallback: use the raw value when non-blank, otherwise the default. */
function resolveEnvUrl(raw: string | undefined, fallback: string): string {
  const trimmed = raw?.trim();
  return trimmed == null || trimmed === "" ? fallback : trimmed;
}

/** Shared `dash()` / `sentinel()` connection options (env defaults per @better-auth/infra). */
export function resolveBetterAuthInfraConnectionOptions(): Pick<
  DashOptions,
  "apiUrl" | "kvUrl" | "apiKey"
> {
  const apiKey = process.env.BETTER_AUTH_API_KEY?.trim();
  if (apiKey == null || apiKey === "") {
    throw new Error("BETTER_AUTH_API_KEY is required but not set.");
  }
  return {
    apiKey,
    apiUrl: normalizeUrl(
      resolveEnvUrl(process.env.BETTER_AUTH_API_URL, DEFAULT_BETTER_AUTH_API_URL),
    ),
    kvUrl: normalizeUrl(resolveEnvUrl(process.env.BETTER_AUTH_KV_URL, DEFAULT_BETTER_AUTH_KV_URL)),
  };
}

/**
 * KV identify endpoint for `sentinelNativeClient` / `sentinelClient` on the Expo client.
 *
 * `EXPO_PUBLIC_BETTER_AUTH_KV_URL` is an OPTIONAL public env var with no schema default, and the
 * staging release env builder (`getStagingExpoEnv`) does not set it. When it isn't inlined into the
 * bundle it resolves to `undefined`, so a bare `!.trim()` here threw "Cannot read property 'trim' of
 * undefined" during the first render that touched `getAuthClient()` — crashing every standalone
 * (staging/production) launch into the StartupErrorBoundary. Fall back to the @better-auth/infra
 * default KV origin instead of asserting a value that isn't guaranteed to be present.
 */
export function resolveBetterAuthKvIdentifyUrl(): string {
  const value = ENV.EXPO_PUBLIC_BETTER_AUTH_KV_URL?.trim();
  if (value == null || value.length === 0) {
    return DEFAULT_BETTER_AUTH_KV_URL;
  }
  return normalizeUrl(value);
}
